// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { screen } from "@bg/XDL/Helpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HomePageContext } from "@insite/content-library/Pages/HomePage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

const enum fields {
    sectionTitle = "sectionTitle",
    sectionDescription = "sectionDescription",
    items = "items",
}

const enum itemFields {
    icon = "icon",
    title = "title",
    description = "description",
    alt = "alt",
}

interface Item {
    fields: {
        title: string;
        icon: string;
        description: string;
        alt: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sectionTitle]: string;
        [fields.sectionDescription]: string;
        [fields.items]: Item[];
    };
}

type Props = OwnProps;

export interface AboutUsServicesWidgetStyles {
    sectionTitle?: TypographyProps;
    sectionDescription?: TypographyProps;
    container?: InjectableCss;
    item?: InjectableCss;
    numberTitleWrapper?: InjectableCss;
    image?: LazyImageProps;
    titleText?: TypographyProps;
    descriptionText?: TypographyProps;
}

export const styles: AboutUsServicesWidgetStyles = {
    sectionTitle: {
        color: "text",
        size: 28,
        lineHeight: "22px",
        weight: "bold",
        css: css`
            letter-spacing: 0;
            height: 37px;
        `,
    },
    sectionDescription: {
        color: "text",
        size: 15,
        lineHeight: "22px",
        weight: "bold",
        css: css`
            letter-spacing: 0;
            margin-bottom: 32px;
        `,
    },
    container: {
        css: css`
            max-width: 659px;
        `,
    },
    item: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0px;
            & {
                margin-bottom: 19px;
            }
        `,
    },
    numberTitleWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-left: 15px;
        `,
    },
    image: {
        cloudinary: false,
        css: css`
            height: 50px;
            width: 50px;
        `,
    },
    titleText: {
        color: "secondary",
        size: 18,
        weight: 500,
        lineHeight: "22px",
        css: css`
            letter-spacing: 0;
            margin-bottom: 8px;
        `,
    },
    descriptionText: {
        color: "text",
        size: 14,
        weight: 500,
        lineHeight: "18px",
        css: css`
            letter-spacing: 0;
            opacity: 0.8;
            ${screen("lte", "xs")`
                max-width: 280px;
                margin-top: 8px;
            `}
        `,
    },
};

const AboutUsServicesWidget = ({ fields }: Props) => {
    return (
        <StyledWrapper
            css={css`
                margin-top: 19px;
                padding-left: 44px;

                ${screen("lte", "sm")`
                    margin-top: 35px;
                    padding-left: 0;
                `}
            `}
        >
            <Typography as="p" {...styles.sectionTitle}>
                {fields.sectionTitle}
            </Typography>
            <Typography as="p" {...styles.sectionDescription}>
                {fields.sectionDescription}
            </Typography>
            <StyledWrapper {...styles.container}>
                {fields.items.map(({ fields: { ...item } }) => (
                    <StyledWrapper key={item.title} {...styles.item}>
                        <LazyImage src={item.icon} altText={item.alt} {...styles.image} />
                        <StyledWrapper {...styles.numberTitleWrapper}>
                            <Typography {...styles.titleText}>{item.title}</Typography>
                            <Typography {...styles.descriptionText}>{item.description}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "About Us Services",
    fieldDefinitions: [
        {
            displayName: "Section Title",
            name: fields.sectionTitle,
            defaultValue: "",
            editorTemplate: "TextField",
            fieldType: "Translatable",
        },
        {
            displayName: "Section Description",
            name: fields.sectionDescription,
            defaultValue: "",
            editorTemplate: "TextField",
            fieldType: "Translatable",
        },
        {
            displayName: "Items",
            name: fields.items,
            editorTemplate: "ListField",
            defaultValue: [],
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    displayName: "Title",
                    name: itemFields.title,
                    defaultValue: "",
                    editorTemplate: "TextField",
                },
                {
                    displayName: "Icon",
                    name: itemFields.icon,
                    defaultValue: "",
                    editorTemplate: "ImagePickerField",
                },
                {
                    displayName: "Image Alt Text",
                    name: itemFields.alt,
                    defaultValue: "",
                    editorTemplate: "TextField",
                },
                {
                    displayName: "Description",
                    name: itemFields.description,
                    defaultValue: "",
                    editorTemplate: "TextField",
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: AboutUsServicesWidget,
    definition,
};

export default widgetModule;
